// run-detached-launcher.ts — 06k C2: cha `run --detach` sinh con tách session
// (`detached: true` ⇒ setsid; đóng cửa sổ/SIGHUP process group của cha không tới
// con), log riêng 0600 trong `runtime/logs/` 0700 (CHỈ con giành lock xoay, giữ 10
// `run-*.log` mới nhất, không xoá log của chính nó — review L-04/L7), rồi BẮT TAY: lock mang `missionId` + pid con ⇒ "yes"; con thoát ⇒ phân
// loại theo `last-run.json` (`run_id`) + mã thoát (B11); hết trần ⇒ "unknown"
// (KHÔNG kill con). Cha không giành lock, không ghi `last-run.json`, không đọc
// stdin (stdin chỉ chuyển cho con khi `--release-secrets-stdin`).
import { spawn } from "node:child_process";
import { randomBytes } from "node:crypto";
import { chmodSync, closeSync, mkdirSync, openSync, readdirSync, rmSync } from "node:fs";
import { constants } from "node:os";
import { join } from "node:path";
import { readMachineLock } from "./mission-lock-file-io.js";
import { realLivenessProbe } from "./process-liveness-probe.js";
import { type ChildErrorCode, childErrorCodeForExit } from "./run-detached-child-exit-codes.js";
import { lastRunPath, newRunId, readLastRun } from "./run-last-outcome-file.js";

const KEEP_LOGS = 10;
const HANDSHAKE_MS = 30_000;
/** stdin secret: reader của con chờ tới 120 s ⇒ trần dài hơn. */
const HANDSHAKE_WITH_STDIN_MS = 150_000;

export interface DetachedRunReceipt {
  started: "yes" | "no" | "unknown";
  run_id: string;
  pid: number;
  log_path: string;
  last_run_path: string;
  exit_code?: number;
  child_error_code?: ChildErrorCode;
  result?: Record<string, unknown>;
}

export interface LaunchDetachedRunInput {
  missionId: string;
  controlRoot: string;
  machineLockPath: string;
  /** Cờ người dùng chuyển tiếp cho con (`--max-ticks`, `--repo`, `--release-secrets-stdin`…). */
  forwardArgs: string[];
  releaseSecretsStdin: boolean;
  /** Mặc định: chính CLI này (`process.execPath process.argv[1]`). */
  command?: { execPath: string; args: string[] };
  env?: NodeJS.ProcessEnv;
  pollMs?: number;
  handshakeTimeoutMs?: number;
}

/** 0 khi đã khởi; mã thoát của con khi "no"; 1 khi "unknown". */
export function detachedReceiptExitCode(receipt: DetachedRunReceipt): number {
  if (receipt.started === "yes") return 0;
  if (receipt.started === "no") return Math.max(receipt.exit_code ?? 1, 1);
  return 1;
}

/** L-04: gọi bởi con ĐÃ giành lock (không supervisor nào khác sống); best-effort. */
export function rotateRunLogs(controlRoot: string, ownLogPath: string | undefined): void {
  try {
    const dir = join(controlRoot, "logs");
    const old = readdirSync(dir)
      .filter((n) => n.startsWith("run-") && n.endsWith(".log") && join(dir, n) !== ownLogPath)
      .sort();
    for (const name of old.slice(0, Math.max(0, old.length - (KEEP_LOGS - 1)))) {
      rmSync(join(dir, name), { force: true });
    }
  } catch {
    // xoay log không bao giờ làm chết run
  }
}

function openRunLog(controlRoot: string): { path: string; fd: number } {
  const dir = join(controlRoot, "logs");
  mkdirSync(dir, { recursive: true, mode: 0o700 });
  chmodSync(dir, 0o700);
  const stamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d+Z$/, "Z");
  const path = join(dir, `run-${stamp}-${randomBytes(3).toString("hex")}.log`);
  return { path, fd: openSync(path, "wx", 0o600) };
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function launchDetachedRun(
  input: LaunchDetachedRunInput,
): Promise<DetachedRunReceipt> {
  const runId = newRunId();
  const log = openRunLog(input.controlRoot);
  const command = input.command ?? { execPath: process.execPath, args: [process.argv[1] ?? ""] };
  const childArgs = ["autopilot", "run", "--mission", input.missionId, "--json"];
  childArgs.push("--detached-child", "--run-id", runId, "--log-path", log.path);
  childArgs.push(...input.forwardArgs);
  let exited: { code: number } | undefined;
  let child: ReturnType<typeof spawn>;
  try {
    child = spawn(command.execPath, [...command.args, ...childArgs], {
      detached: true,
      stdio: [input.releaseSecretsStdin ? "inherit" : "ignore", log.fd, log.fd],
      env: input.env ?? process.env,
    });
  } finally {
    closeSync(log.fd); // con giữ bản sao của fd; cha không giữ log mở
  }
  child.once("error", () => {
    exited ??= { code: 1 };
  });
  child.once("exit", (code, signal) => {
    exited = { code: code ?? 128 + (signal ? (constants.signals[signal] ?? 0) : 0) };
  });
  const base = {
    run_id: runId,
    pid: child.pid ?? -1,
    log_path: log.path,
    last_run_path: lastRunPath(input.controlRoot),
  };
  const cap =
    input.handshakeTimeoutMs ??
    (input.releaseSecretsStdin ? HANDSHAKE_WITH_STDIN_MS : HANDSHAKE_MS);
  const deadline = Date.now() + cap;
  try {
    while (Date.now() < deadline) {
      if (exited !== undefined)
        return { ...base, ...(await classifyExit(input, runId, exited.code)) };
      const lock = await readMachineLock(input.machineLockPath);
      if (lock?.missionId === input.missionId && lock.pid === child.pid) {
        return { ...base, started: "yes" };
      }
      await sleep(input.pollMs ?? 200);
    }
    if (exited !== undefined)
      return { ...base, ...(await classifyExit(input, runId, exited.code)) };
    return { ...base, started: "unknown" };
  } finally {
    child.unref();
  }
}

async function classifyExit(
  input: LaunchDetachedRunInput,
  runId: string,
  code: number,
): Promise<Omit<DetachedRunReceipt, "run_id" | "pid" | "log_path" | "last_run_path">> {
  const last = await readLastRun(input.controlRoot);
  if (last?.run_id === runId && last.state === "exited") {
    return { started: "yes", exit_code: code, ...(last.result ? { result: last.result } : {}) };
  }
  // N15: run của mình đã ghi "running" nhưng chết trước khi ghi "exited".
  if (last?.run_id === runId)
    return { started: "no", exit_code: Math.max(code, 1), child_error_code: "other" };
  const mapped = childErrorCodeForExit(code);
  // N16 (+ L3): last-run "running" CHỈ là bận khi lock thuộc mission này và pid còn sống.
  const lock = await readMachineLock(input.machineLockPath);
  const busy =
    mapped === "other" &&
    last?.state === "running" &&
    lock?.missionId === input.missionId &&
    (await realLivenessProbe.isAlive(lock.pid));
  return {
    started: "no",
    exit_code: Math.max(code, 1),
    child_error_code: busy ? "machine_lock_busy" : mapped,
  };
}
